/**
 * ============================================================
 * KEJJA OS — src/hooks/useTheme.ts
 * Manages dark/light/system theme. Persists to localStorage.
 * Applies class to <html> for Tailwind's dark: variant.
 * ============================================================
 */

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'kejja.theme';

function getSystemPref(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

function applyThemeToDOM(theme: Theme): 'light' | 'dark' {
  const effective: 'light' | 'dark' = theme === 'system' ? getSystemPref() : theme;
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
  }
  root.style.colorScheme = effective;
  return effective;
}

/**
 * Initialize theme immediately (before React mounts).
 * Call this from main.tsx at the top of the file, or inline in index.html.
 */
export function initializeTheme(): 'light' | 'dark' {
  const theme = readStoredTheme();
  return applyThemeToDOM(theme);
}

/**
 * Main hook. Returns current theme + setter.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() =>
    theme === 'system' ? getSystemPref() : theme,
  );

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    const effective = applyThemeToDOM(next);
    setEffectiveTheme(effective);
  }, []);

  // Listen to system theme changes when set to 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const effective = applyThemeToDOM('system');
      setEffectiveTheme(effective);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Ensure DOM stays in sync (in case multiple hook instances exist)
  useEffect(() => {
    const effective = applyThemeToDOM(theme);
    setEffectiveTheme(effective);
  }, [theme]);

  return { theme, effectiveTheme, setTheme };
}
